import { SPENDING_CONTROL_TYPES } from "@clipboard-health/contract-backend-main";
import { requiredEnumWithFallback } from "@clipboard-health/contract-core";
import { z } from "zod";

/**
 * Schema for the `2026-01-configure-spending-controls` flag.
 *
 * `enabledControlTypes` gates which control-type rows (total vs variableCharge/Boosts) the
 * spending-controls UI renders. Elements use `requiredEnumWithFallback` (matching the contract's
 * own control-type fields) so an unknown type added in LaunchDarkly coerces to `ENUM_FALLBACK`
 * instead of failing validation.
 *
 * NOTE: `useCbhFlag` returns the raw LaunchDarkly value and applies neither this Zod `.default`
 * nor the fallback coercion, so consumers must coalesce a missing `enabledControlTypes` to
 * `["total"]` and drop types they don't recognize themselves. `internalUseOnly` is retained:
 * menu/navigation access gating depends on it.
 */
export const spendingControlsConfigurationSchema = z.discriminatedUnion("enabled", [
  z.object({ enabled: z.literal(false) }),
  z.object({
    enabled: z.literal(true),
    spendingControlDurationInDays: z.number().int().min(1).default(7),
    internalUseOnly: z.boolean(),
    enabledControlTypes: z
      .array(requiredEnumWithFallback(SPENDING_CONTROL_TYPES))
      .min(1)
      .default(["total"]),
  }),
]);

export type SpendingControlsConfiguration = z.infer<typeof spendingControlsConfigurationSchema>;
